/* ---------- Hero: the question beside a sight line, drawn ---------- */
.hero {
  /* Same overlap as the home hero: cancel the flow space the sticky header reserves. */
  margin-top: calc(-1 * (var(--header-h) + 2 * var(--header-margin)));
  padding-block: calc(var(--header-h) + 2 * var(--header-margin) + var(--sp-12)) var(--sp-20);
  background: var(--surface-alt);
}
.heroGrid {
  display: grid;
  gap: var(--sp-12);
  align-items: center;
}
.heroText > * {
  max-width: 38rem;
}
.heroTitle {
  /* Room under the breadcrumb, which the dropped eyebrow label used to provide. */
  margin-top: var(--sp-6);
  font-size: clamp(2.4rem, 3.4vw + 1rem, 4.25rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  text-wrap: balance;
}
.heroLead {
  margin-top: var(--sp-5);
  font-size: var(--fs-lg);
  color: var(--text);
}
.heroActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-4) var(--sp-8);
  margin-top: var(--sp-8);
}
.heroLink {
  font-weight: 600;
  color: var(--text-strong);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.3em;
}
.heroLink:hover {
  color: var(--accent-text);
}
.heroImage {
  border-radius: var(--radius-lg);
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-lg);
}
@media (min-width: 64rem) {
  .heroGrid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
    gap: var(--sp-16);
  }
}

/* ---------- The cost of waiting: three figures on hairlines ---------- */
.figures {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 14rem), 1fr));
  gap: var(--sp-6) var(--sp-10);
  margin: 0 0 var(--sp-16);
  padding: 0;
  list-style: none;
}
.figures li {
  display: grid;
  gap: var(--sp-2);
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.figures strong {
  font-family: var(--font-display);
  font-size: clamp(2rem, 1.2rem + 2.2vw, 3rem);
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--text-strong);
}
.figures span {
  color: var(--text);
}

/* ---------- The signature: when each approach finds the same hazard ----------
   Phones first: a vertical track down the left, the stages beside it. The track runs
   from the accent (found on the design, a revision) to the danger colour (found in the
   crash data, a reconstruction): the later a hazard is found, the more it costs. */
.timeline {
  position: relative;
  display: grid;
  gap: var(--sp-8);
  margin: 0;
  padding: 0 0 0 var(--sp-10);
  list-style: none;
}
.timeline::before {
  content: "";
  position: absolute;
  top: 0.4rem;
  bottom: 0.4rem;
  left: 0.55rem;
  width: 2px;
  background: linear-gradient(180deg, var(--accent), var(--danger));
}
.timeline li {
  position: relative;
  display: grid;
  gap: var(--sp-2);
}
/* The stage's stop on the track. */
.timeline li::before {
  content: "";
  position: absolute;
  top: 0.2rem;
  left: calc(-1 * var(--sp-10) + 0.15rem);
  width: 0.8rem;
  height: 0.8rem;
  border: 2px solid var(--text-muted);
  border-radius: 50%;
  background: var(--surface);
}
.timeline li[data-mark]::before {
  top: 0;
  left: calc(-1 * var(--sp-10) - 0.05rem);
  width: 1.2rem;
  height: 1.2rem;
  border-width: 3px;
}
.timeline li[data-mark="audit"]::before {
  border-color: var(--accent);
  background: var(--accent);
}
.timeline li[data-mark="crash"]::before {
  border-color: var(--danger);
  background: var(--danger);
}
.stageTitle {
  font-family: var(--font-display);
  font-size: var(--fs-lg);
  font-weight: 600;
  color: var(--text-strong);
}
.stageText {
  color: var(--text);
}
/* Who finds the hazard at this stage, and what fixing it costs then. */
.finding {
  margin: var(--sp-2) 0 0;
  padding: var(--sp-3) var(--sp-4);
  border-left: 3px solid var(--accent);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  color: var(--text-strong);
  font-weight: 600;
}
.timeline li[data-mark="crash"] .finding {
  border-left-color: var(--danger);
  background: color-mix(in srgb, var(--danger) 10%, transparent);
}

/* Wide screens: the stages in a row along one horizontal track. */
@media (min-width: 64rem) {
  .timeline {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: var(--sp-6);
    padding: 2.25rem 0 0;
  }
  .timeline::before {
    top: 0.55rem;
    bottom: auto;
    left: 0;
    right: 0;
    width: auto;
    height: 2px;
    background: linear-gradient(90deg, var(--accent), var(--danger));
  }
  .timeline li {
    align-content: start;
  }
  .timeline li::before {
    top: calc(-2.25rem + 0.15rem);
    left: 0;
  }
  .timeline li[data-mark]::before {
    top: calc(-2.25rem - 0.05rem);
    left: -0.2rem;
  }
}

/* ---------- What an audit is: the heading beside the prose ---------- */
.split {
  display: grid;
  gap: var(--sp-6) var(--sp-16);
}
.splitTitle {
  margin: 0;
  max-width: 14ch;
}
.splitBody {
  display: grid;
  gap: var(--sp-4);
  max-width: 44rem;
  font-size: var(--fs-md);
}
.splitBody p {
  margin: 0;
}
@media (min-width: 64rem) {
  .split {
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  }
}

/* ---------- New designs and existing roads, side by side ---------- */
.roads {
  display: grid;
  gap: var(--sp-12) var(--sp-10);
}
.roads article {
  display: grid;
  align-content: start;
  gap: var(--sp-3);
}
.roads h3 {
  margin: var(--sp-3) 0 0;
}
.roads p {
  margin: 0;
  max-width: 34rem;
  color: var(--text);
}
.roadImage {
  border-radius: var(--radius-lg);
  box-shadow: 0 0 0 1px var(--border);
}
@media (min-width: 48rem) {
  .roads {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* ---------- What you gain: two by two on hairlines, no boxes ---------- */
.gains {
  display: grid;
  gap: var(--sp-10) var(--sp-12);
  margin: 0;
  padding: 0;
  list-style: none;
}
.gains li {
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.gains h3 {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-xl);
}
.gains p {
  margin: 0;
  max-width: 36rem;
  color: var(--text);
}
@media (min-width: 48rem) {
  .gains {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
